import React, { memo } from "react";
import { Flex, Button, HStack } from "@chakra-ui/react";


type PaginatorProps = {
  pagesCount: number;
  currentPage: number;
  setCurrentPage: (value: React.SetStateAction<number>) => void
  pages: number[];
}

export const Paginator: React.FC<PaginatorProps> = memo(({pagesCount, currentPage, setCurrentPage, pages}) => {
  const handlePageChange = (nextPage: number): void => {
    // ページ番号が有効範囲内かチェック
    if (nextPage >= 1 && nextPage <= pagesCount) {
      setCurrentPage(nextPage);
    }
  };

  const canPrev = currentPage > 1;
  const canNext = currentPage < pagesCount;

  // 不正なページ番号を除外し、有効なページのみを表示
  const validPages = pages.filter(page => page > 0 && page <= pagesCount);

  // 表示するページのリストを計算（10ページ以上の場合は「最初・前・現在・次・最後」のみ）
  const displayedPages = React.useMemo(() => {
    if (pagesCount >= 10) {
      const candidateSet = new Set<number>([
        1,
        pagesCount,
        currentPage - 1,
        currentPage,
        currentPage + 1,
      ]);
      // 範囲外を除外
      const filtered = Array.from(candidateSet).filter(
        (p) => p >= 1 && p <= pagesCount
      );
      // 数字順にソート
      return filtered.sort((a, b) => a - b);
    }
    // 10未満なら全ページ表示
    return validPages;
  }, [pagesCount, currentPage, validPages]);

  return (
    <Flex
      aria-label="pagination navigation"
      as="nav"
      className="pagination-container"
      align="center"
      justify="space-between"
      alignItems='center'
      w="full"
    >
      <Button
        size='sm'
        variant='ghost'
        colorScheme='blue'
        color="blue.500"
        isDisabled={!canPrev}
        _hover={{ color: "blue.300" }}
        onClick={() => canPrev && handlePageChange(currentPage - 1)}
      >
        ＜
      </Button>

      <HStack align="center" m='0' spacing={1}>
        {(() => {
          const items: React.ReactNode[] = [];
          let prev: number | null = null;

          const renderPageButton = (page: number) => {
            const isCurrent = page === currentPage;
            return (
              <Button
                key={`pagination_page_${page}`}
                w={7}
                h={8}
                fontSize="sm"
                onClick={() => handlePageChange(page)}
                bg={isCurrent ? "blue.500" : "gray.300"}
                color={isCurrent ? "white" : "inherit"}
                _hover={{ bg: "blue.500", color: "white" }}
                aria-current={isCurrent ? "page" : undefined}
              >
                {page}
              </Button>
            );
          };

          displayedPages.forEach((page) => {
            if (prev !== null && page - prev > 1) {
              // ギャップがある場合は省略記号を挿入
              items.push(
                <Button
                  key={`pagination_ellipsis_${prev}_${page}`}
                  w={7}
                  h={8}
                  variant="ghost"
                  isDisabled
                  aria-hidden="true"
                >
                  …
                </Button>
              );
            }
            items.push(renderPageButton(page));
            prev = page;
          });

          return items;
        })()}
      </HStack>

      <Button
        size='sm'
        variant='ghost'
        colorScheme='blue'
        color="blue.500"
        isDisabled={!canNext}
        _hover={{ color: "blue.300" }}
        onClick={() => canNext && handlePageChange(currentPage + 1)}
      >
        ＞
      </Button>
    </Flex>
  );
});

